import React from "react";
import { Row } from "reactstrap";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import { formatDate } from "helpers/constant";
import { formatTime24Hour } from "common/service/CommonService";

interface AdverseEventDetailViewTabProps {
  data: any;
  id: string | undefined;
}

const AdverseEventDetailViewTab: React.FC<AdverseEventDetailViewTabProps> = ({ data, id }) => {
  return (
    <>
      {/* Section 1: General & Timing */}
      <SectionCard
        icon="ri-hospital-line"
        title="General & Timing Details"
        subtitle="Details regarding the participant, project, and event dates."
      >
        <Row>
          <DetailField label="Adverse Event No." value={data.adverseeventnumber || `AE-${id}`} truncate={true} />
          <DetailField label="Study Code" value={data.studycode} truncate={true} />
          <DetailField label="Subject No." value={data.subjectno} truncate={true} />
          <DetailField
            label="Period No."
            value={
              (data.periodno || data.period)
                ? String(data.periodno || data.period).replace(/Period\s*/i, "")
                : "—"
            }
            truncate={true}
          />
          <DetailField label="Onset of Event" value={data.onsetofevent ? (data.onsetofevent.toLowerCase().replace(" ", "") === "predose" ? "Pre dose" : "Post dose") : "—"} truncate={true} />
          <DetailField label="Onset Date" value={formatDate(data.onsetdate)} truncate={true} />
          <DetailField label="Onset Time" value={formatTime24Hour(data.onsettime)} truncate={true} />
          <DetailField label="Reporting Date" value={formatDate(data.reportingdate)} truncate={true} />
          <DetailField label="Reporting Time" value={formatTime24Hour(data.reportingtime)} truncate={true} />
          <DetailField label="Recording Date" value={formatDate(data.recordingdate)} truncate={true} />
          <DetailField label="Recording Time" value={formatTime24Hour(data.recordingtime)} truncate={true} />
        </Row>
      </SectionCard>

      {/* Section 2: Event Assessment */}
      <SectionCard
        icon="ri-alert-line"
        title="Event Assessment & Occurrence"
        subtitle="Key severity markers, occurrence, and last dose information."
      >
        <Row>
          <DetailField label="Severity" value={data.severityname || data.severitygradename || data.severity || data.severitygrade} truncate={true} />
          <DetailField label="Report Method" value={data.reportmethodname || data.reportmethod} truncate={true} />
          <DetailField label="Occurrence" value={data.occurencename || data.occurrencename || data.occurence || data.occurrence} truncate={true} />
          <DetailField label="Last Dose Date" value={formatDate(data.lastdosereceiveddate || data.lastdosedate)} truncate={true} />
          <DetailField label="Last Dose Time" value={formatTime24Hour(data.lastdosereceivedtime || data.lastdosetime)} truncate={true} />
          <DetailField label="Event Term / Description" value={data.description || data.eventterm} columns={12} expandable={true} />
        </Row>
      </SectionCard>

      {/* Section 3: Clinical Examination & Vital Signs */}
      <SectionCard
        icon="ri-stethoscope-line"
        title="Clinical Examination & Vital Signs"
        subtitle="Record exam timing, blood pressure, pulse, respiratory rate, and temperature."
      >
        <Row>
          <DetailField label="Exam Start Time" value={formatTime24Hour(data.medicalexamstarttime || data.medicalexamstart)} truncate={true} />
          <DetailField label="Exam End Time" value={formatTime24Hour(data.medicalexamendtime || data.medicalexamend)} truncate={true} />
          <DetailField label="Blood Pressure" value={(data.bloodpressure || data.bp) ? `${data.bloodpressure || data.bp} mm of Hg` : null} truncate={true} />
          <DetailField label="Pulse Rate" value={data.pulse ? `${data.pulse} bpm` : null} truncate={true} />
          <DetailField label="Respiratory Rate" value={data.respiratoryrate ? `${data.respiratoryrate} breaths/min` : null} truncate={true} />
          <DetailField label="Temperature" value={data.temperature ? `${data.temperature} °${data.temperatureunit || "C"}${data.temperaturelocation ? ` (${data.temperaturelocation})` : ""}` : null} truncate={true} />
          <DetailField label="Associated with other symptoms?" value={data.othersymptoms === true ? "Yes" : (data.othersymptoms === false ? "No" : (data.associatedsymptoms === "yes" ? "Yes" : "No"))} truncate={true} />
          <DetailField label="Symptoms Remark" value={data.othersymptomsremark || data.symptomsremark} truncate={true} />
        </Row>
      </SectionCard>

      {/* Section 4: General Examination */}
      <SectionCard
        icon="ri-heart-pulse-line"
        title="General Examination"
        subtitle="Record general and systemic examinations, provisional diagnosis, and actions taken."
      >
        <Row>
          <DetailField label="General Examination" value={data.generalexamination ? data.generalexamination.toUpperCase() : null} truncate={true} />
          <DetailField label="Systemic Examination" value={data.systemicexamination ? data.systemicexamination.toUpperCase() : null} truncate={true} />
          <DetailField label="Investigation Advised" value={data.investigationadvised} truncate={true} />
          <DetailField label="Diagnosis" value={data.diagnosisname || data.diagnosis} truncate={true} />
          <DetailField label="Diagnosis Remarks" value={data.diagnosisremarks} truncate={true} />
          <DetailField label="Action Taken" value={data.actiontakenname || data.actiontaken} truncate={true} />
          <DetailField
            label="Is Followup required?"
            value={
              data.isfollowuprequired === true ||
              String(data.isfollowuprequired).toLowerCase() === "yes" ||
              String(data.isfollowuprequired).toLowerCase() === "true"
                ? "Yes"
                : data.isfollowuprequired === false ||
                  String(data.isfollowuprequired).toLowerCase() === "no" ||
                  String(data.isfollowuprequired).toLowerCase() === "false"
                ? "No"
                : "—"
            }
            truncate={true}
          />
          <DetailField label="Remark" value={data.remarks || data.examremark} columns={12} expandable={true} />
        </Row>
      </SectionCard>

      {/* Verification Details */}
      {data.isverified && (
        <SectionCard
          icon="ri-checkbox-circle-line"
          title="Verification Details"
          subtitle="Verification and electronic signature information for this adverse event."
        >
          <Row>
            <div className="col-md-6">
              <label className="form-label text-muted fw-medium fs-12 mb-2 text-uppercase ls-1 d-block">
                Verified By
              </label>
              <div className="d-flex align-items-center gap-2 p-1">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    background: "#e8f0fe",
                    color: "#3c6bc4",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {data.verifiedbyname
                    ?.split(" ")
                    ?.map((n: string) => n[0])
                    ?.join("")
                    ?.toUpperCase()
                    ?.slice(0, 2) || "AE"}
                </div>
                <div>
                  <div className="fw-bold text-dark" style={{ fontSize: 14, lineHeight: 1.2 }}>
                    {data.verifiedbyname || "—"}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {data.verifieddate ? formatDate(data.verifieddate) : "—"}
                  </div>
                </div>
              </div>
            </div>
          </Row>
        </SectionCard>
      )}
    </>
  );
};

export default AdverseEventDetailViewTab;
